import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/api/client';
import { useSession } from '@/stores/session';
import { useNotificationPreferences, type NotificationCategory } from '@/stores/notificationPreferences';
import { useNotificationConnection } from '@/stores/notificationConnection';
import { playNotificationTone, speakNotification } from './notificationAudio';
import { legacyHref } from './nav';
import s from './NotificationRuntime.module.css';

type Notice = { id: string; type: string; title: string; body?: string | null; orderId?: string | null; actionUrl?: string | null; soundKey?: string | null };
function category(type: string): NotificationCategory {
  if (type.startsWith('ORDER_CHANGE_')) return 'change';
  if (type.startsWith('ORDER_STOCK_') || type.startsWith('PURCHASE_')) return 'stock';
  if (type === 'ORDER_SHIPPED' || type.startsWith('SHIPMENT_')) return 'shipped';
  if (type === 'ORDER_FOLLOW_REQUIRED') return 'follow';
  return 'review';
}

export function NotificationRuntime() {
  const user = useSession((state) => state.user);
  const navigate = useNavigate();
  const [queue, setQueue] = useState<Notice[]>([]);
  const [banner, setBanner] = useState<Notice | null>(null);
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const speechById = useRef<Record<string, string>>({});
  const setConnected = useNotificationConnection((state) => state.setConnected);

  useEffect(() => {
    if (user?.id) void useNotificationPreferences.getState().load(user.id);
  }, [user?.id]);
  useEffect(() => { setQueue([]); setBanner(null); speechById.current = {}; }, [user?.id]);
  useEffect(() => {
    const retry = () => setReconnectNonce((value) => value + 1);
    window.addEventListener('ims:v2:retry-stream', retry);
    return () => window.removeEventListener('ims:v2:retry-stream', retry);
  }, []);
  useEffect(() => {
    if (!user?.id) return;
    const seen = new Set<string>();
    const receive = (items: Notice[]) => {
      const fresh = items.filter((item) => {
        if (!item || typeof item.id !== 'string' || seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      if (seen.size > 5000) seen.clear();
      if (!fresh.length) return;
      setQueue((current) => [...current, ...fresh]);
      window.dispatchEvent(new Event('ims:v2:notification'));
      const preferences = useNotificationPreferences.getState().value;
      for (const item of fresh) {
        if (!preferences.sound || !preferences.categories[category(item.type)]) continue;
        void playNotificationTone(item.soundKey || undefined).catch(() => undefined);
        if (preferences.speech) {
          const sentence = `${user.name || user.username}，${item.title}`;
          speechById.current[item.id] = sentence;
          window.setTimeout(() => speakNotification(sentence), 380);
        }
      }
    };
    const source = new EventSource('/api/v2/notifications/stream');
    source.addEventListener('ready', () => setConnected(true));
    source.addEventListener('notifications', (event) => {
      try {
        const parsed: unknown = JSON.parse((event as MessageEvent).data);
        if (Array.isArray(parsed)) receive(parsed as Notice[]);
      } catch { /* malformed event */ }
    });
    source.onerror = () => setConnected(false);
    const onPush = (event: MessageEvent<{ type?: string; payload?: Notice }>) => {
      if (event.data?.type === 'trade-inventory:web-push' && event.data.payload) receive([event.data.payload]);
    };
    navigator.serviceWorker?.addEventListener('message', onPush);
    return () => {
      source.close();
      setConnected(false);
      navigator.serviceWorker?.removeEventListener('message', onPush);
    };
  }, [user?.id, user?.name, user?.username, setConnected, reconnectNonce]);
  useEffect(() => {
    if (banner || !queue.length) return;
    setBanner(queue[0]);
    setQueue((current) => current.slice(1));
  }, [banner, queue]);
  useEffect(() => {
    if (!banner) return;
    if (banner.type.startsWith('ORDER_STOCK_') || banner.type.startsWith('ORDER_CHANGE_')) return;
    const timer = window.setTimeout(() => setBanner(null), 8000);
    return () => window.clearTimeout(timer);
  }, [banner]);

  const open = async () => {
    if (!banner) return;
    if (import.meta.env.VITE_READONLY === '0') await api.patch(`/api/v2/notifications/${encodeURIComponent(banner.id)}/read`, {}).catch(() => undefined);
    const orderId = banner.orderId;
    const action = banner.actionUrl;
    setBanner(null);
    if (orderId) navigate(`/orders/${encodeURIComponent(orderId)}`);
    else if (action?.startsWith('/orders/') && !action.startsWith('//')) navigate(action);
    else if (action?.startsWith('/') && !action.startsWith('//')) window.location.assign(legacyHref(action));
    else navigate('/notifications');
  };

  if (!banner) return null;
  const sticky = banner.type.startsWith('ORDER_STOCK_') || banner.type.startsWith('ORDER_CHANGE_');
  return <section className={s.banner} role="status" aria-label="新通知">
    <div className={s.title}><span className={s.brand}>发</span><b>进销存 · {category(banner.type) === 'stock' ? '库存提醒' : '实时提醒'}</b><small>刚刚</small></div>
    <strong>{banner.title}</strong>
    {banner.body && <p>{banner.body}</p>}
    {speechById.current[banner.id] && <p className={s.speaking}>正在播报：「{speechById.current[banner.id]}」</p>}
    <div className={s.actions}><button type="button" onClick={() => setBanner(null)}>{sticky ? '稍后处理' : '关闭'}</button><button type="button" onClick={() => void open()}>查看订单</button></div>
  </section>;
}
